import { useCallback, useEffect, useState } from "react";
import { Button as UIButton, Tooltip } from "@mui/material";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import DeleteBin2LineIcon from "remixicon-react/DeleteBin2LineIcon";
import { createUseStyles } from "$utils/createUseStyles";
import { Badge, Modal, Table, Text } from "$components";
import { IProduct } from "$containers";
import NumberInput from "$containers/EDB/NumberInput";
import useJStyles from "$hooks/useJStyles";
import { ProductService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

const useStyles = createUseStyles(() => ({
  productDesignation: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    "-webkit-box-orient": "vertical",
    "-webkit-line-clamp": 2,
  },
  deleteButton: {
    color: "rgba(0, 0, 0) !important",
    border: "none !important",
    "&:hover": {
      color: "rgba(0, 0, 0) !important",
      backgroundColor: "rgba(0, 0, 0, 0.04) !important",
    },
    "&:disabled": {
      color: "rgba(0, 0, 0, 0.26) !important",
    },
    "& svg": {
      color: "inherit !important",
    },
  },
}));

type Props = {
  companyId: string;
  cart: any;
  handleClickProduct: (product: IProduct) => void;
  addToCart: (product: any, value: number) => void;
  removeFromCart: (product: any) => void;
};

function TableCart({
  companyId,
  cart,
  handleClickProduct,
  addToCart,
  removeFromCart,
}: Props) {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [productToDelete, setProductToDelete] = useState<any>(null);
  const [cartProducts, setCartProducts] = useState([]);

  // open and close cart dialog confirmation
  const handleProductDelete = (product: any) => {
    setOpenConfirmDelete(true);
    setProductToDelete(product);
  };

  const handleCancelDialog = () => {
    setOpenConfirmDelete(false);
  };

  const handleValidateDialog = () => {
    setOpenConfirmDelete(false);
    removeFromCart(productToDelete._id);
  };

  const getCartProducts = useCallback(async () => {
    if (!companyId) {
      setCartProducts([]);
      return;
    }

    const notRefProducts = [];
    const refProducts = [];
    await Promise.all(
      cart.map(async (p: any) => {
        if (p.productID) {
          const productPromise = await queryClient.fetchQuery({
            queryKey: [
              CacheInvalidationAction.GET_PRODUCT_STOCK,
              p.productID,
              companyId,
            ],

            queryFn: () =>
              ProductService.getProductStock({
                id: p.productID,
                company: companyId,
              }),

            gcTime: 5 * 60 * 1000,
          });
          const product = productPromise.data[0];
          if (product) {
            product.qty = p.qty;

            if (product.isReferenced) {
              refProducts.push(product);
            } else {
              notRefProducts.push(product);
            }
          } else {
            notRefProducts.push(p);
          }
        }
      })
    );

    refProducts.sort((a, b) =>
      (a.designation || "").localeCompare(b.designation || "")
    );
    notRefProducts.sort((a, b) =>
      (a.designation || "").localeCompare(b.designation || "")
    );

    setCartProducts([refProducts, notRefProducts]);
  }, [cart, companyId]);

  const getThead = (index: number) => [
    {
      id: "designation",
      label: index === 0 ? "Produits catalogue" : "Produits hors catalogue",
      width: 500,
    },
    {
      id: "ref",
      label: "Référence",
      width: 250,
    },
    {
      id: "unit",
      label: "Unité",
      width: 150,
    },
    {
      id: "value",
      label: "Quantité",
      width: 120,
    },
    {
      id: "stockStatus",
      label: "Stock",
    },
    {
      id: "stockWarning",
      label: "",
    },
    {
      id: "button",
      label: "",
      width: 10,
    },
  ];

  const renderCartProduct = (products: any[]) => {
    return products.map((product: any) => [
      {
        value: (
          <Text type="important" className={classes.productDesignation}>
            {product.designation}
          </Text>
        ),
        onClickAction: () => handleClickProduct(product),
      },
      {
        value: (
          <Text>
            {(product.isReferenced !== false && product.reference) || "-"}
          </Text>
        ),
        onClickAction: () => handleClickProduct(product),
      },
      {
        value: product.unit,
        onClickAction: () => handleClickProduct(product),
      },
      {
        value: (
          <NumberInput
            value={product.qty}
            onChange={(val) =>
              addToCart(product._id || product.id || product.productID, val)
            }
          />
        ),
      },
      {
        value:
          !!product?.isReferenced &&
          ((product?.stock?.quantity || 0) > 0 ? (
            <Badge>
              Dispo (
              {(product?.stock?.quantity ?? 0) -
                (product?.stock?.edbReservedQuantity ?? 0)}
              )
            </Badge>
          ) : (
            <Badge color="red">Rupture de stock</Badge>
          )),
        onClickAction: () => handleClickProduct(product),
      },
      {
        value: !!product?.isReferenced &&
          product?.qty > product?.stock?.quantity && (
            <Tooltip
              title="La quantité demandée est supérieure à la quantité disponible"
              placement="top"
            >
              <ReportProblemOutlined style={{ color: "red" }} />
            </Tooltip>
          ),
        onClickAction: () => handleClickProduct(product),
      },
      {
        value: (
          <UIButton
            variant="text"
            style={{ textTransform: "none" }}
            onClick={() => handleProductDelete(product)}
            className={classes.deleteButton}
          >
            <DeleteBin2LineIcon />
          </UIButton>
        ),
      },
    ]);
  };

  useEffect(() => {
    getCartProducts();
  }, [getCartProducts]);

  return (
    <div>
      {cart && cart.length > 0 && (
        <div>
          {cartProducts.map((filteredCat: any, index: number) => (
            <div key={index}>
              {filteredCat && filteredCat.length > 0 && (
                <Table
                  rowsPerPage={filteredCat.length}
                  bodyData={renderCartProduct(filteredCat)}
                  thead={getThead(index)}
                  usePaper={false}
                />
              )}
            </div>
          ))}
        </div>
      )}
      <Modal
        title="Confirmation de suppression"
        open={openConfirmDelete}
        onCancel={handleCancelDialog}
        onValidate={handleValidateDialog}
      >
        <div className={jclasses.jDialogContent}>
          <Text>Êtes vous sûr de vouloir continuer ?</Text>
        </div>
      </Modal>
    </div>
  );
}

export default TableCart;
